/*!
 * bb-recommended-products.js — BarrierBoss native SMART cross-sell (fallback)
 * Replaces the Vitals recommended-products widget. NO app, NO libraries,
 * vanilla + defer.
 *
 * The rich "Complete Your Build" complementary cards are rendered SERVER-SIDE in
 * snippets/bb-recommended-products.liquid (full metafield access: real ratings,
 * price, availability, unit). This script is ONLY the graceful fallback for
 * products whose type has no complementary mapping (sauna/roofing/etc.): it
 * renders Shopify's native /recommendations (intent=related) into the same rich
 * card markup so every PDP still shows something dynamic.
 *
 * If the container is already server-populated (data-mode="complementary" / grid
 * has children), this bails. Idempotent, graceful-empty, self-hiding.
 */
(function () {
  'use strict';

  function fmtMoney(cents, currency, locale) {
    var v = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(locale || undefined, {
        style: 'currency', currency: currency || 'USD',
        minimumFractionDigits: (v % 1 === 0 ? 0 : 2)
      }).format(v);
    } catch (e) { return '$' + v.toFixed(2); }
  }

  function sizedImg(url, w) {
    if (!url) return '';
    if (url.indexOf('{width}') > -1) return url.replace('{width}', w);
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // Title-derived unit, mirroring the server-side Liquid logic.
  function unitOf(title) {
    // strip 'corrugated' first — it contains the substring 'gate' (corru-GATE-d)
    var t = String(title || '').toLowerCase().replace(/corrugated/g, '');
    if (t.indexOf('kit') > -1 || t.indexOf('complete system') > -1) return 'Kit';
    if (t.indexOf('gate') > -1) return 'Gate';
    if (t.indexOf('u-channel') > -1 || t.indexOf('track') > -1 || t.indexOf('channel') > -1) return 'Set';
    if (t.indexOf('post') > -1) return 'Post';
    if (t.indexOf('sheet') > -1) return 'Sheet';
    return 'Panel';
  }

  // Rating from a record IF exposed (recommendations JSON normally isn't -> null,
  // stars skipped by design; we never call a review app).
  function ratingOf(p) {
    var val = null, cnt = null;
    try {
      if (p.metafields && p.metafields.reviews) {
        var r = p.metafields.reviews.rating;
        val = r && (r.value != null ? r.value : r);
        if (val && typeof val === 'object' && val.value != null) val = val.value;
        cnt = p.metafields.reviews.rating_count;
      }
    } catch (e) {}
    if (p.rating != null && val == null) { val = p.rating; cnt = p.rating_count; }
    val = parseFloat(val); cnt = parseInt(cnt, 10);
    if (!val || val <= 0) return null;
    return { avg: val, count: (cnt > 0 ? cnt : null) };
  }

  function card(p, currency, locale) {
    var img = sizedImg(p.featured_image || (p.images && p.images[0] && (p.images[0].src || p.images[0])) || '', 400);
    var price = fmtMoney(p.price, currency, locale);
    var unit = unitOf(p.title);
    var avail = (p.available === false)
      ? '<span class="bb-rec-avail bb-rec-oos">Sold out</span>'
      : '<span class="bb-rec-avail bb-rec-instock">Available</span>';
    var rat = ratingOf(p);
    var rating = '';
    if (rat) {
      var pct = Math.max(0, Math.min(100, (rat.avg / 5) * 100));
      rating = '<span class="bb-rec-rating" aria-label="Rated ' + rat.avg + ' out of 5">' +
        '<span class="bb-rec-stars"><span class="bb-rec-stars-off">★★★★★</span>' +
        '<span class="bb-rec-stars-on" style="width:' + pct.toFixed(1) + '%">★★★★★</span></span>' +
        '<span class="bb-rec-rnum">' + rat.avg.toFixed(1) + '</span>' +
        (rat.count ? '<span class="bb-rec-rcnt">' + rat.count + ' review' + (rat.count === 1 ? '' : 's') + '</span>' : '') +
        '</span>';
    }
    var h = '<div class="bb-rec-card">';
    h += '<a class="bb-rec-imglink" href="' + esc(p.url) + '" tabindex="-1" aria-hidden="true"><span class="bb-rec-imgwrap">';
    if (img) h += '<img class="bb-rec-img" src="' + esc(img) + '" alt="' + esc(p.title) + '" loading="lazy" width="400" height="400">';
    h += '</span></a><div class="bb-rec-body">';
    h += '<a class="bb-rec-titlelink" href="' + esc(p.url) + '"><span class="bb-rec-title">' + esc(p.title) + '</span></a>';
    h += rating;
    h += '<span class="bb-rec-price">from <span class="bb-rec-money">' + price + '</span> <span class="bb-rec-unit">/per ' + unit + '</span></span>';
    h += avail;
    h += '<a class="bb-rec-cta" href="' + esc(p.url) + '">Choose Options <span aria-hidden="true">→</span></a>';
    h += '</div></div>';
    return h;
  }

  function build(root) {
    if (!root || root.getAttribute('data-bb-rec-done') === '1') return;
    // Server already rendered complementary cards? bail.
    if (root.getAttribute('data-mode') === 'complementary') { root.setAttribute('data-bb-rec-done', '1'); return; }
    var grid = root.querySelector('.bb-rec-grid');
    if (grid && grid.children.length) { root.setAttribute('data-bb-rec-done', '1'); return; }

    var pid = root.getAttribute('data-product-id');
    if (!pid) return;
    root.setAttribute('data-bb-rec-done', '1'); // idempotent guard

    var limit = parseInt(root.getAttribute('data-limit'), 10) || 4;
    var currency = root.getAttribute('data-currency') || 'USD';
    var locale = root.getAttribute('data-locale') || undefined;
    // routes.root_url ("/" primary, "/fr" subpath market) — strip trailing slash
    // so we never emit "//..." (protocol-relative -> wrong host).
    var rootUrl = (root.getAttribute('data-base') || '').replace(/\/+$/, '');
    var pidStr = String(pid);

    fetch(rootUrl + '/recommendations/products.json?product_id=' + encodeURIComponent(pid) +
          '&limit=' + limit + '&intent=related', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        var items = (data && data.products) ? data.products : [];
        items = items.filter(function (p) { return String(p.id) !== pidStr; }).slice(0, limit);
        if (!items.length || !grid) { root.style.display = 'none'; return; }
        grid.innerHTML = items.map(function (p) { return card(p, currency, locale); }).join('');
        root.removeAttribute('hidden');
        root.classList.add('bb-rec-ready');
      })
      .catch(function () { root.style.display = 'none'; });
  }

  function init() {
    var roots = document.querySelectorAll('.bb-rec[data-mode="related"]');
    for (var i = 0; i < roots.length; i++) build(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
